import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAssessment } from "../components/AssessmentProvider";

export type WorkspaceRecentType = "object" | "risk" | "mitigation";

export type WorkspaceRecentItem = {
  id: number;
  type: WorkspaceRecentType;
  title: string;
  viewedAt: number;
  deleted?: boolean;
  objectType?: "ZONE" | "CONDUIT" | "COMPONENT";
};

type RecentTitle = { id: number; title: string; objectType?: WorkspaceRecentItem["objectType"] };

type Ctx = {
  items: WorkspaceRecentItem[];
  recordViewedItem: (item: Omit<WorkspaceRecentItem, "viewedAt" | "deleted">) => void;
  syncTitlesForType: (type: WorkspaceRecentType, list: RecentTitle[]) => void;
  clearRecents: () => void;
};

const MAX_RECENTS = 25;

const WorkspaceRecentsContext = createContext<Ctx | null>(null);

export function WorkspaceRecentsProvider({ children }: { children: React.ReactNode }) {
  const { selectedId } = useAssessment();
  const [items, setItems] = useState<WorkspaceRecentItem[]>([]);

  useEffect(() => {
    setItems([]);
  }, [selectedId]);

  const clearRecents = useCallback(() => {
    setItems([]);
  }, []);

  const recordViewedItem = useCallback((item: Omit<WorkspaceRecentItem, "viewedAt" | "deleted">) => {
    setItems((prev) => {
      const next = prev.filter((entry) => !(entry.type === item.type && entry.id === item.id));
      const updated: WorkspaceRecentItem = {
        ...item,
        viewedAt: Date.now(),
        deleted: false,
      };
      return [updated, ...next].slice(0, MAX_RECENTS);
    });
  }, []);

  const syncTitlesForType = useCallback((type: WorkspaceRecentType, list: RecentTitle[]) => {
    const titleById = new Map<number, string>();
    const objectTypeById = new Map<number, WorkspaceRecentItem["objectType"]>();
    for (const entry of list) {
      titleById.set(entry.id, entry.title);
      if (entry.objectType) objectTypeById.set(entry.id, entry.objectType);
    }
    setItems((prev) =>
      prev.map((item) => {
        if (item.type !== type) return item;
        const title = titleById.get(item.id);
        if (!title) {
          return { ...item, deleted: true };
        }
        const nextObjectType = item.type === "object" ? objectTypeById.get(item.id) ?? item.objectType : item.objectType;
        if (item.title === title && item.deleted !== true && item.objectType === nextObjectType) return item;
        return { ...item, title, deleted: false, objectType: nextObjectType };
      })
    );
  }, []);

  const value = useMemo<Ctx>(
    () => ({ items, recordViewedItem, syncTitlesForType, clearRecents }),
    [items, recordViewedItem, syncTitlesForType, clearRecents]
  );

  return <WorkspaceRecentsContext.Provider value={value}>{children}</WorkspaceRecentsContext.Provider>;
}

export function useWorkspaceRecents() {
  const ctx = useContext(WorkspaceRecentsContext);
  if (!ctx) throw new Error("useWorkspaceRecents must be used within WorkspaceRecentsProvider");
  return ctx;
}
